/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/ResultsDropdown/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import {
  DEFAULT_RESULTS_LIMIT,
  getResultsLimitOptions,
} from "../../utils/resultsLimitOptions.js";

const ResultsDropdown = ({ onResultsLimitChange }) => {
  const { t, i18n } = useTranslation();
  const { deviceType } = useDeviceDetection();
  const [selectedValue, setSelectedValue] = useState(DEFAULT_RESULTS_LIMIT);

  const resultsOptions = useMemo(
    () => getResultsLimitOptions(t),
    [t, i18n.language],
  );

  const selectedOption = useMemo(
    () =>
      resultsOptions.find((option) => option.value === selectedValue) ??
      resultsOptions[0],
    [resultsOptions, selectedValue],
  );

  const handleChange = (option) => {
    setSelectedValue(option.value);
    onResultsLimitChange(option);
  };

  const getContainerWidth = () => {
    switch (deviceType) {
      case "smallMobile":
        return "150px";
      case "mediumMobile":
        return "160px";
      default:
        return "192px";
    }
  };

  const getContainerMarginLeft = () => {
    switch (deviceType) {
      case "smallMobile":
        return "-14px";
      case "mediumMobile":
        return "-12px";
      default:
        return "0px";
    }
  };

  return (
    <div className="resultsSelector">
      <Select
        options={resultsOptions}
        value={selectedOption}
        isSearchable={false}
        menuPlacement="auto"
        onChange={handleChange}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            border: "1px solid #231F20",
            borderRadius: "8px",
            padding: "0px 8px",
            minHeight: "32px",
            boxShadow: "none",
            "&:hover": {
              borderColor: "#888",
              cursor: "pointer",
            },
          }),
          container: (baseStyles) => ({
            ...baseStyles,
            width: getContainerWidth(),
            marginLeft: getContainerMarginLeft(),
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: "0 8px",
          }),
          singleValue: (baseStyles) => ({
            ...baseStyles,
            color: "#333",
            fontSize: "16px",
          }),
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            color: "#333",
            paddingRight: "8px",
            "&:hover": {
              color: "#111",
            },
          }),
          indicatorSeparator: () => ({
            display: "none",
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            cursor: "pointer",
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 9999,
          }),
        }}
      />
    </div>
  );
};

export default ResultsDropdown;
